/* @jsx h */
import { defineComponent, h, computed, ref, watch } from "vue";
import { RouterLink, RouterView, useRoute } from "vue-router";
import { useCustomer } from "@app/vue/providers/Auth";
import { useLocalization } from "@lemmonhq/uikit";
import { SeoHead } from "../../components/SeoHead";
import { PublicFooterComponent } from "../../components/PublicFooterComponent";
import { CustomerLanguageSelector } from "../../components/CustomerLanguageSelector";
import { useBranding } from "../../providers/Branding";
import { CollectedBottomNav } from "../CollectedPage/CollectedBottomNav";

export const AccountProfilePage = defineComponent({
  name: "AccountProfilePage",
  setup() {
    const route = useRoute();
    const { customer, showAuthDialog } = useCustomer();
    const { t } = useLocalization();
    const { branding } = useBranding();

    const tabs = [
      {
        id: "profile",
        labelKey: "customer.account.profile",
        icon: "person",
        path: "profile",
      },
      {
        id: "addresses",
        labelKey: "customer.account.addresses",
        icon: "location_on",
        path: "addresses",
      },
      {
        id: "orders",
        labelKey: "customer.account.orders",
        icon: "history",
        path: "orders",
      },
      {
        id: "settings",
        labelKey: "customer.account.settings",
        icon: "settings",
        path: "settings",
      },
    ];

    const displayName = computed(() => {
      if (!customer.value) return t("customer.account.guest");
      if (
        !customer.value.anon &&
        "name" in customer.value &&
        customer.value.name?.trim()
      ) {
        return customer.value.name.trim();
      }
      if (
        !customer.value.anon &&
        "email" in customer.value &&
        customer.value.email
      ) {
        return customer.value.email.split("@")[0];
      }
      return t("customer.account.guest");
    });

    const isGuest = computed(() => Boolean(customer.value?.anon));

    const isActiveTab = (tabId: string) => {
      return route.path.endsWith(tabId) ||
        (route.path === "/account" && tabId === "profile")
        ? true
        : false;
    };

    const initial = computed(() => displayName.value.charAt(0).toUpperCase());

    // Google sign-ups carry a profile picture; fall back to the initial when
    // there is none or the remote image fails to load.
    const avatarUrl = computed(() =>
      customer.value && !customer.value.anon && "avatar_url" in customer.value
        ? customer.value.avatar_url || null
        : null,
    );
    const avatarFailed = ref(false);
    watch(avatarUrl, () => (avatarFailed.value = false));

    return () => (
      <div class="min-h-screen bg-base-200/40 pb-24 text-base-content sm:pb-28">
        <SeoHead
          title={t("customer.account.seo_title")}
          description={t("customer.account.seo_description")}
          keywords={t("customer.account.seo_keywords")}
          type="profile"
        />

        {/* Reserve a full viewport so the footer never peeks in and then jumps
            down while a subpage is still loading. */}
        <main class="mx-auto min-h-[100dvh] w-full max-w-2xl px-4">
          {/* Compact identity header with language selector */}
          <section class="mt-4 rounded-[1.75rem] bg-base-100 p-4 shadow-sm ring-1 ring-base-content/5">
            <div class="flex items-center gap-3">
              <div class="grid size-12 shrink-0 place-items-center overflow-hidden rounded-full bg-gradient-to-br from-primary to-primary/60 text-xl font-semibold text-primary-content shadow-md shadow-primary/20">
                {avatarUrl.value && !avatarFailed.value ? (
                  <img
                    src={avatarUrl.value}
                    alt=""
                    class="size-full object-cover"
                    referrerpolicy="no-referrer"
                    onError={() => (avatarFailed.value = true)}
                  />
                ) : (
                  initial.value
                )}
              </div>
              <div class="min-w-0 flex-1">
                <p class="text-[11px] font-medium text-base-content/45">
                  {t("customer.account.account_center")}
                </p>
                <h1 class="truncate text-xl font-bold leading-tight tracking-tight">
                  {displayName.value}
                </h1>
              </div>
              <CustomerLanguageSelector
                variant="compact"
                brandId={branding.value?.id}
              />
            </div>
            {isGuest.value && (
              <div class="mt-3 flex items-center gap-3 border-t border-base-content/5 pt-3">
                <p class="min-w-0 flex-1 text-sm text-base-content/60">
                  {t("customer.account.guest_prompt")}
                </p>
                <button
                  type="button"
                  class="btn btn-primary btn-sm shrink-0 gap-1.5 rounded-full"
                  onClick={() => showAuthDialog()}
                >
                  <span
                    class="material-symbols-outlined text-lg"
                    aria-hidden="true"
                  >
                    lock
                  </span>
                  {t("customer.account.sign_in")}
                </button>
              </div>
            )}
          </section>

          {/* Anchor the router scrolls back to when switching tabs. */}
          <div data-account-tabs-anchor aria-hidden="true" />
          {/* Sub-navigation: sticky glass segmented control */}
          <div class="sticky top-0  -mx-4 mt-1 px-2 py-2">
            <nav class="scrollbar-hide flex gap-1 overflow-x-auto rounded-full bg-base-100/80 p-1 shadow-sm ring-1 ring-base-content/5 backdrop-blur-xl backdrop-saturate-150">
              {tabs.map((tab) => {
                const active = isActiveTab(tab.id);
                return (
                  <RouterLink
                    key={tab.id}
                    to={`/account/${tab.path}`}
                    aria-current={active ? "page" : undefined}
                    class={[
                      "flex min-w-max flex-auto items-center justify-center gap-1.5 rounded-full px-3.5 py-2 text-sm font-semibold transition-colors duration-200",
                      active
                        ? "bg-base-content text-base-100 shadow-sm"
                        : "text-base-content/55 hover:text-base-content",
                    ]}
                  >
                    <span
                      class="material-symbols-outlined text-lg"
                      aria-hidden="true"
                    >
                      {tab.icon}
                    </span>
                    <span>{t(tab.labelKey)}</span>
                  </RouterLink>
                );
              })}
            </nav>
          </div>

          {/* Active subpage */}
          <div class="mt-3 rounded-[1.75rem] bg-base-100 p-5 shadow-sm ring-1 ring-base-content/5 sm:p-7">
            <RouterView />
          </div>
        </main>

        <div class="mt-10">
          <PublicFooterComponent />
        </div>

        <CollectedBottomNav />
      </div>
    );
  },
});
